/**
 * DeathCam — the screen seven of eight paying players end up looking at.
 *
 * GAME-DESIGN §5: "Their screen is not allowed to be nothing." A player who just
 * lost a buy-in and gets a black screen has been given a reason to close the
 * tab, and requeue is where the session either continues or ends.
 *
 * The sequence:
 *   1. KILLER VIEW for 4s — the camera follows whoever killed you, so the death
 *      is legible rather than mysterious. Falls straight through to free camera
 *      when the zone killed you, because there is nobody to watch.
 *   2. FREE CAMERA — fly the arena and watch the rest of the match.
 *   3. LIVE POT TICKER — what the survivors are still fighting over, updated
 *      from the room's Schema for as long as the match runs.
 *   4. ONE-TAP REQUEUE — a single control, thumb-sized, always in the same spot.
 *
 * Spectator control of other players is deliberately absent: this is the dead
 * player's screen, not a spectator mode.
 */

import { LOBBY_URL } from '../config.js';
import { cameraState } from '../state.js';
import { formatEth } from './CombatHUD.js';

const KILLER_VIEW_MS = 4000;

export class DeathCam {
  constructor(cameraController, { onRequeue } = {}) {
    this.controller = cameraController;
    this.root = document.getElementById('death-cam');
    this.active = false;
    this._phase = 'idle';
    this._since = 0;
    this._onRequeue = onRequeue ?? (() => window.location.assign(LOBBY_URL));
    this._build();
  }

  _build() {
    this.root.innerHTML = `
      <div class="dc-banner">
        <div class="headline" data-headline>ELIMINATED</div>
        <div class="sub" data-sub></div>
      </div>
      <div class="dc-hint" data-hint>WASD / drag to fly · watching the match</div>
      <div class="dc-panel">
        <div class="dc-stat"><div class="label">Placement</div><div class="value" data-place>—</div></div>
        <div class="dc-stat"><div class="label">Kills</div><div class="value" data-kills>0</div></div>
        <div class="dc-stat"><div class="label">Damage</div><div class="value" data-damage>0</div></div>
        <div class="dc-stat"><div class="label">Live pot</div><div class="value" data-pot>—</div></div>
        <button class="dc-btn" data-requeue>REQUEUE</button>
      </div>
    `;
    this.headlineEl = this.root.querySelector('[data-headline]');
    this.subEl = this.root.querySelector('[data-sub]');
    this.hintEl = this.root.querySelector('[data-hint]');
    this.placeEl = this.root.querySelector('[data-place]');
    this.killsEl = this.root.querySelector('[data-kills]');
    this.damageEl = this.root.querySelector('[data-damage]');
    this.potEl = this.root.querySelector('[data-pot]');

    const btn = this.root.querySelector('[data-requeue]');
    // Both events: `click` alone has a ~300ms delay on some mobile browsers, and
    // this is the one control that has to feel instant.
    btn.addEventListener('click', () => this._onRequeue());
    btn.addEventListener(
      'touchend',
      (e) => {
        e.preventDefault();
        this._onRequeue();
      },
      { passive: false }
    );
  }

  /**
   * @param {object} info { killerId, killerName, placement, kills, damageDealt }
   */
  show(info) {
    this.active = true;
    this.root.classList.add('active');
    this._since = performance.now();

    // The gym revives and re-kills; without this a death after a shown result
    // keeps the previous round's "WINNER" headline.
    this.headlineEl.textContent = 'ELIMINATED';
    this.headlineEl.style.color = '';

    this.placeEl.textContent = info.placement ? `#${info.placement}` : '—';
    this.killsEl.textContent = String(info.kills ?? 0);
    this.damageEl.textContent = String(Math.round(info.damageDealt ?? 0));

    if (info.killerId) {
      this._phase = 'killer';
      this.subEl.textContent = `by ${info.killerName ?? 'an opponent'}`;
      this.hintEl.textContent = 'watching your killer';
      cameraState.free = false;
      cameraState.followId = info.killerId;
    } else {
      // Nobody to watch. Skipping straight to free camera beats four seconds of
      // staring at your own corpse.
      this.subEl.textContent = 'by the zone';
      this._toFreeCamera();
    }
  }

  _toFreeCamera() {
    this._phase = 'free';
    this.hintEl.textContent = 'WASD / drag to fly · watching the match';
    this.controller.detachToFreeCamera();
  }

  update(now) {
    if (!this.active) return;
    if (this._phase === 'killer' && now - this._since >= KILLER_VIEW_MS) {
      this._toFreeCamera();
    }
  }

  /** The pot the survivors are still playing for. Wei in, display out. */
  setPot(potWei) {
    this.potEl.textContent = formatEth(potWei);
  }

  /** Match over while dead: swap the banner for the result. */
  showResult({ winnerName, isWinner }) {
    this.active = true;
    this.root.classList.add('active');
    this.headlineEl.textContent = isWinner ? 'WINNER' : 'MATCH OVER';
    this.headlineEl.style.color = isWinner ? '#ffd93d' : '#ff6076';
    this.subEl.textContent = winnerName ? `${winnerName} takes the pot` : '';
    this.hintEl.textContent = '';
    this._phase = 'result';
  }

  hide() {
    this.active = false;
    this._phase = 'idle';
    this.root.classList.remove('active');
    this.controller.attachToPlayer();
  }
}
